import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Select, DatePicker, message, Spin } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const TaskForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchProjects();
    if (id) {
      fetchTask();
    }
  }, [id]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/project_board/projects/');
      setProjects(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching projects');
    }
  };

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/project_board/tasks/${id}/`);
      const task = response.data;

      form.setFieldsValue({
        ...task,
        due_date: task.due_date ? dayjs(task.due_date) : null,
      });
    } catch (error) {
      message.error('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      const formData = {
        ...values,
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
      };

      if (id) {
        await axios.put(`/project_board/tasks/${id}/`, formData);
        message.success('Task updated successfully');
      } else {
        await axios.post('/project_board/tasks/', formData);
        message.success('Task created successfully');
      }
      navigate('/tasks');
    } catch (error) {
      message.error('Failed to save task');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <h2>{id ? 'Edit Task' : 'Create New Task'}</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          name="project"
          label="Project"
          rules={[{ required: true, message: 'Please select a project' }]}
        >
          <Select placeholder="Select project">
            {projects.map(project => (
              <Option key={project.id} value={project.id}>{project.name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="title"
          label="Task Title"
          rules={[{ required: true, message: 'Please enter task title' }]}
        >
          <Input placeholder="Enter task title" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
        >
          <TextArea rows={4} placeholder="Enter task description" />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
        >
          <Select placeholder="Select priority">
            <Option value={0}>Low</Option>
            <Option value={1}>Medium</Option>
            <Option value={2}>High</Option>
            <Option value={3}>Urgent</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="due_date"
          label="Due Date"
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        {/* TODO: Add stage, assignees, labels, checklists, cover image, attachments */}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
            {id ? 'Update Task' : 'Create Task'}
          </Button>
          <Button style={{ marginLeft: '8px' }} onClick={() => navigate('/tasks')}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default TaskForm;
